import { useCallback, useEffect, useRef, useState } from 'react';
import { doGET } from '../util/HttpUtil';
import { PARSE_STATE } from './useExtractionGate';

// Persisted document-extraction record status (backend DocumentExtraction):
//   0 = REQUESTED (still running), 1 = RECEIVED (done), 2 = ERROR (failed).
export const EXTRACTION_RECORD_STATUS = {
    REQUESTED: 0,
    RECEIVED: 1,
    ERROR: 2,
};

const isTerminalRecord = (row) =>
    row?.status === EXTRACTION_RECORD_STATUS.RECEIVED
    || row?.status === EXTRACTION_RECORD_STATUS.ERROR;

// Re-derives a terminal parse state from the persisted extraction records so the
// document-first gate can open after a page reload. The live parse state only
// exists on the socket (useDocumentParseProgress); it is NOT persisted, so once
// the page reloads the socket snapshot is gone and the gate would otherwise stay
// closed with "Continue" disabled even though extraction already finished. This
// hook reads /api/document-extraction/grid and, from the MOST RECENT run, returns:
//   - PARSE_STATE.COMPLETED when the latest extraction run has succeeded,
//   - PARSE_STATE.FAILED    when the latest run has errored,
//   - null                  when the latest run is still running, or none was dispatched.
// It also serves as a fallback when the realtime socket is unavailable, since
// callers poll refetchExtractionStatus while waiting for the parse to finish.
const useExtractionStatus = (formId, { enabled = true, refreshKey = 0 } = {}) => {
    const [fetchedParseState, setFetchedParseState] = useState(null);
    const cancelledRef = useRef(false);

    const fetchStatus = useCallback(async () => {
        if (!enabled || !formId) return;
        try {
            const res = await doGET(
                `/api/document-extraction/grid?form_id=${formId}&rows=10&page=1`,
            );
            if (cancelledRef.current || res?.status !== 200) return;
            const rows = Array.isArray(res?.data?.rows)
                ? res.data.rows
                : (Array.isArray(res?.data) ? res.data : []);
            if (rows.length === 0) {
                setFetchedParseState(null);
                return;
            }
            // Extraction is retried, so a form can accumulate several records: older
            // errors, orphaned 'requested' runs that never resolved, and the final run.
            // The authoritative outcome is the MOST RECENT run by requestedAt — its
            // terminal status is what counts; earlier orphaned/errored rows are ignored.
            const latest = rows.reduce(
                (a, b) => ((b?.requestedAt ?? 0) >= (a?.requestedAt ?? 0) ? b : a),
            );
            if (!isTerminalRecord(latest)) {
                setFetchedParseState(null);
                return;
            }
            setFetchedParseState(
                latest.status === EXTRACTION_RECORD_STATUS.ERROR
                    ? PARSE_STATE.FAILED
                    : PARSE_STATE.COMPLETED,
            );
        } catch (e) {
            // Non-fatal: fall back to the socket / persisted application state.
        }
    }, [enabled, formId]);

    useEffect(() => {
        cancelledRef.current = false;
        fetchStatus();
        return () => {
            cancelledRef.current = true;
        };
    }, [fetchStatus, refreshKey]);

    return { fetchedParseState, refetchExtractionStatus: fetchStatus };
};

export default useExtractionStatus;
